/**
 * EditDialog - A reusable edit dialog component for CustomGrid
 *
 * Renders a modal dialog with form fields pre-populated from row data.
 * Supports text, email, number, date, and select field types.
 * Validates required fields and custom validators before saving.
 *
 * Props:
 *   open        - Boolean to control dialog visibility
 *   onClose     - Callback when dialog is closed/cancelled
 *   onSave      - Callback with edited values: onSave(editedValues, originalRow)
 *   row         - The row data object being edited
 *   fields      - Array of field definitions:
 *                  { name, label, type, required, disabled, validator, validatorMessage, options, maxLength }
 *   title       - Dialog title (default: "Edit Record")
 *   saveLabel   - Save button label (default: "Save")
 *   columns     - Number of form columns (default: 1, use 2 for wider dialogs)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, TextField, FormControl, Select, MenuItem, Grid, Checkbox, ListItemText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const EditDialog = ({
    open,
    onClose,
    onSave,
    row = {},
    fields = [],
    title = 'Edit Record',
    saveLabel = 'Save',
    columns = 3
}) => {
    const [formValues, setFormValues] = useState({});
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const gridItemSize = Math.floor(12 / columns);

    const getOptionValue = useCallback((option) => (
        option && typeof option === 'object' ? option.value : option
    ), []);

    const getOptionLabel = useCallback((option) => (
        option && typeof option === 'object' ? option.label : option
    ), []);

    const getSelectedLabel = useCallback((options, selectedValue) => {
        const selectedOption = options?.find(option => getOptionValue(option) === selectedValue);
        return selectedOption ? getOptionLabel(selectedOption) : selectedValue;
    }, [getOptionLabel, getOptionValue]);

    // Initialize form values from row when dialog opens
    useEffect(() => {
        if (open && row) {
            const initial = {};
            fields.forEach(field => {
                let val = row[field.name] ?? '';
                // For multiselect fields, ensure value is an array (API may return comma-separated string)
                if (field.type === 'multiselect') {
                    if (typeof val === 'string') {
                        val = val ? val.split(',').map(s => s.trim()).filter(Boolean) : [];
                    } else if (!Array.isArray(val)) {
                        val = [];
                    }
                }
                initial[field.name] = val;
            });
            setFormValues(initial);
            setErrors({});
            setTouched({});
            setSubmitted(false);
        }
    }, [open, row, fields]);

    const handleChange = useCallback((fieldName, value) => {
        setFormValues(prev => ({ ...prev, [fieldName]: value }));
    }, []);

    const handleBlur = useCallback((fieldName) => {
        setTouched(prev => ({ ...prev, [fieldName]: true }));
    }, []);

    // Validate all fields and return errors map
    const getErrors = useCallback(() => {
        const newErrors = {};
        fields.forEach(field => {
            const value = formValues[field.name];
            const isRequired = field.required || (typeof field.requiredIf === 'function' && field.requiredIf(formValues));
            // Required check (strings, arrays, and falsy values)
            if (isRequired) {
                const isEmpty = !value
                    || (typeof value === 'string' && value.trim() === '')
                    || (Array.isArray(value) && value.length === 0);
                if (isEmpty) {
                    newErrors[field.name] = `${field.label} is required`;
                    return;
                }
            }
            // Custom validator
            if (field.validator) {
                const errorMsg = field.validator(value, formValues);
                if (errorMsg) {
                    newErrors[field.name] = errorMsg;
                }
            }
        });
        return newErrors;
    }, [fields, formValues]);

    // Re-validate whenever form values change (keeps errors and Save button state in sync)
    useEffect(() => {
        setErrors(getErrors());
    }, [getErrors]);

    // Compute whether all required fields are filled (for disabling Save button)
    const hasRequiredGaps = useMemo(() => {
        return fields.some(field => {
            const value = formValues[field.name];
            const isRequired = field.required || (typeof field.requiredIf === 'function' && field.requiredIf(formValues));
            if (!isRequired) return false;
            return !value
                || (typeof value === 'string' && value.trim() === '')
                || (Array.isArray(value) && value.length === 0);
        });
    }, [fields, formValues]);

    const handleSave = useCallback(() => {
        setSubmitted(true);
        const validationErrors = getErrors();
        if (Object.keys(validationErrors).length > 0) return;
        if (onSave) {
            onSave(formValues, row);
        }
    }, [getErrors, onSave, formValues, row]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        }
    }, [handleSave]);

    const renderField = (field) => {
        const value = formValues[field.name] ?? '';
        const showError = (touched[field.name] || submitted) && !!errors[field.name];
        const isRequired = field.required || (typeof field.requiredIf === 'function' && field.requiredIf(formValues));

        if (field.type === 'multiselect' && field.options) {
            const selectedValues = Array.isArray(value) ? value : (typeof value === 'string' && value ? value.split(',').map(s => s.trim()).filter(Boolean) : []);
            return (
                <Grid item xs={12} sm={gridItemSize} key={field.name}>
                    <div className="cgrid-adv-field">
                        <label className={isRequired ? 'required' : ''}>
                            {field.label}
                        </label>
                        <FormControl fullWidth size="small" error={showError}>
                            <Select
                                multiple
                                value={selectedValues}
                                onChange={(e) => handleChange(field.name, e.target.value)}
                                onBlur={() => handleBlur(field.name)}
                                disabled={field.disabled}
                                displayEmpty
                                renderValue={(selected) => selected?.length ? selected.join(', ') : `Select ${field.label}`}
                            >
                                {field.options.map(opt => (
                                    <MenuItem key={opt} value={opt} sx={{ py: 0.5, px: 2 }}>
                                        <Checkbox checked={selectedValues.indexOf(opt) > -1} size="small" />
                                        <ListItemText primary={opt} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {showError && <p className="cgrid-field-error">{errors[field.name]}</p>}
                    </div>
                </Grid>
            );
        }

        if (field.type === 'select' && field.options) {
            return (
                <Grid item xs={12} sm={gridItemSize} key={field.name}>
                    <div className="cgrid-adv-field">
                        <label className={isRequired ? 'required' : ''}>
                            {field.label}
                        </label>
                        <FormControl fullWidth size="small" error={showError}>
                            <Select
                                value={value}
                                onChange={(e) => handleChange(field.name, e.target.value)}
                                onBlur={() => handleBlur(field.name)}
                                disabled={field.disabled}
                                displayEmpty
                                renderValue={(selected) => selected ? getSelectedLabel(field.options, selected) : `Select ${field.label}`}
                            >
                                {field.options.map(opt => (
                                    <MenuItem key={getOptionValue(opt)} value={getOptionValue(opt)} sx={{ py: 1, px: 2 }}>
                                        {getOptionLabel(opt)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {showError && <p className="cgrid-field-error">{errors[field.name]}</p>}
                    </div>
                </Grid>
            );
        }

        return (
            <Grid item xs={12} sm={gridItemSize} key={field.name}>
                <div className={`cgrid-adv-field ${field.disabled ? 'disabled' : ''}`}>
                    <label className={isRequired ? 'required' : ''}>
                        {field.label}
                    </label>
                    <TextField
                        fullWidth
                        size="small"
                        type={field.type || 'text'}
                        value={value}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        onBlur={() => handleBlur(field.name)}
                        onKeyDown={handleKeyDown}
                        disabled={field.disabled}
                        error={showError}
                        placeholder={field.placeholder || ''}
                        InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                        inputProps={field.maxLength ? { maxLength: field.maxLength } : undefined}
                    />
                    {showError && <p className="cgrid-field-error">{errors[field.name]}</p>}
                </div>
            </Grid>
        );
    };

    const maxWidthMap = { 1: 'sm', 2: 'md', 3: 'lg', 4: 'xl' };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidthMap[columns] || 'md'}
            fullWidth
            className="cgrid-edit-dialog"
            PaperProps={{ className: 'cgrid-edit-dialog-paper' }}
        >
            <DialogTitle className="cgrid-adv-search-title">
                <span>{title}</span>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className="cgrid-adv-search-content">
                <Grid container spacing={2}>
                    {fields.map(renderField)}
                </Grid>
            </DialogContent>
            <DialogActions className="cgrid-adv-search-actions">
                <Button
                    onClick={onClose}
                    className="cgrid-btn cgrid-btn-cancel"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    className="cgrid-btn cgrid-btn-apply"
                    disabled={hasRequiredGaps}
                >
                    {saveLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditDialog;
